// One memory card in the Vault list — extracted from Vault.tsx (kept verbatim).
// Pure presentation: all actions come in as callbacks.

import { useT } from '../lib/i18n.js';
import { TIER_COLOR } from '../lib/tiers.js';
import { ThumbButtons } from './ThumbButtons.js';
import type { Memory } from '../lib/memoryModel.js';

export function MemoryCard({ m, isOwner, selectMode, selected, translatedText, busy, onOpen, onToggleSelect, onTranslate, onShowOriginal, onThumbUp, onThumbDown }: {
  m: Memory;
  isOwner: boolean;
  selectMode: boolean;
  selected: boolean;
  translatedText?: string;
  busy: { translate: boolean; review: boolean; align: boolean };
  onOpen: () => void;
  onToggleSelect: () => void;
  onTranslate: () => void;
  onShowOriginal: () => void;
  onThumbUp: () => void;
  onThumbDown: () => void;
}) {
  const t = useT();
  return (
    <li
      onClick={() => { if (!isOwner) return; selectMode ? onToggleSelect() : onOpen(); }}
      className={`border rounded-xl p-5 space-y-2 transition-colors ${isOwner ? 'cursor-pointer' : ''} ${
        selectMode && selected ? 'border-amber/60 bg-amber/[0.05]' : 'border-ink/10 hover:border-ink/25'
      }`}
    >
      <div className="flex items-center justify-between text-[11.5px] text-ink/40">
        <span className="flex items-center gap-2">
          {selectMode && (
            <span className={`w-4 h-4 rounded grid place-items-center border ${selected ? 'bg-amber border-amber text-void' : 'border-ink/30'}`}>
              {selected ? '✓' : ''}
            </span>
          )}
          {m.approxYear ?? '—'}
          {m.lifeChapter ? ` · ${m.lifeChapter.replace(/_/g, ' ')}` : ''}
        </span>
        <span className="flex items-center gap-1.5">
          {m.reviewed && (
            <span className="px-2 py-0.5 rounded-full border text-[9px] text-emerald-300 border-emerald-300/40">✓ reviewed</span>
          )}
          {m.source === 'auto' && (
            <span className={`px-2 py-0.5 rounded-full border text-[9px] ${m.reviewStatus === 'pending' ? 'text-particle border-particle/40' : 'text-ink/40 border-ink/15'}`}>
              ✨ auto{m.reviewStatus === 'pending' ? ' · review' : ''}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full border text-[9px] ${TIER_COLOR[m.visibility] ?? 'text-ink/50 border-ink/20'}`}>
            {m.visibility.replace('_', ' ')}
          </span>
        </span>
      </div>
      {m.title && <div className="font-serif text-lg text-ink/90">{m.title}</div>}
      <p className="font-serif text-base text-ink/70 leading-relaxed line-clamp-3">{translatedText ?? m.body}</p>
      {m.source === 'auto' && m.meta && (m.meta.newsUrl || m.meta.newsExcerpt || m.meta.newsSource) && (
        <div className="rounded-lg border border-sky-300/15 bg-sky-300/[0.03] px-3 py-2 space-y-1">
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-ink/45">
            <span className="text-sky-300/70">référence</span>
            {m.meta.newsSource && <span>{m.meta.newsSource}</span>}
            {m.meta.autoConfidence != null && <span>· confiance {m.meta.autoConfidence}%</span>}
            {m.meta.newsUrl && (
              <a href={m.meta.newsUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                className="text-sky-300/90 hover:text-sky-300 hover:underline normal-case tracking-normal">lire l’article ↗</a>
            )}
          </div>
          {m.meta.newsExcerpt && <p className="font-serif italic text-[12px] text-ink/45 leading-snug line-clamp-2">“{m.meta.newsExcerpt}”</p>}
        </div>
      )}
      {(m.themes.length > 0 || m.people.length > 0) && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {m.people.map((p) => (
            <span key={`p-${p}`} className="px-2 py-0.5 rounded-full border border-amber/20 text-[9px]  text-amber/70">{p}</span>
          ))}
          {m.themes.map((th) => (
            <span key={th} className="px-2 py-0.5 rounded-full border border-ink/10 text-[9px]  text-ink/50">{th}</span>
          ))}
        </div>
      )}
      {!selectMode && (
        <div className="flex items-center justify-end gap-2 pt-1">
          {translatedText ? (
            <button onClick={(e) => { e.stopPropagation(); onShowOriginal(); }}
              title="Show the original language"
              className="px-2.5 h-7 grid place-items-center rounded-full border border-ink/15 text-[10px] text-ink/45 hover:text-ink">{t('common.original')}</button>
          ) : (
            <button onClick={(e) => { e.stopPropagation(); onTranslate(); }} disabled={busy.translate}
              title={t('common.translate')}
              className="w-7 h-7 grid place-items-center rounded-full border border-ink/15 text-ink/40 text-[12px] hover:text-sky-300 hover:border-sky-300/40 disabled:opacity-40">🌐</button>
          )}
          <ThumbButtons
            onUp={onThumbUp} onDown={onThumbDown}
            upActive={m.reviewed} busyUp={busy.review} busyDown={busy.align}
            upTitle={m.reviewed ? 'Reviewed & trusted — click to undo' : 'Thumbs up — mark reviewed/trusted (reinforces the twin)'}
            downTitle="Thumbs down — send to Align to confirm/correct" />
          {/* downColor defaults to amber = the Coffre's "send to Align" meaning */}
        </div>
      )}
    </li>
  );
}
